import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { logout } from "./auth";
import "./ActivationPage.css";

const LOCAL_API_URL =
  process.env.REACT_APP_LOCAL_API_URL || "http://localhost:7000";

const maskLicence = (key) => {
  if (!key) return "-";

  const value = String(key);

  if (value.length <= 8) return value;

  return `${value.slice(0, 4)}••••${value.slice(-4)}`;
};

const normalizeComPort = (value) => {
  return String(value || "").trim().toUpperCase();
};

const isValidComPort = (value) => {
  const normalized = normalizeComPort(value);

  if (!normalized) {
    return true;
  }

  return /^COM\d+$/.test(normalized);
};

function ActivationPage() {
  const navigate = useNavigate();

  const [licenseKey, setLicenseKey] = useState("");
  const [drawerComPort, setDrawerComPort] = useState("");
  const [currentConfig, setCurrentConfig] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [activating, setActivating] = useState(false);
  const [savingPort, setSavingPort] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const pendingSales = Number(
    syncStatus?.pendingSales || syncStatus?.pending_sales || 0
  );

  const loadCurrentStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      setError("");

      const [configRes, syncRes] = await Promise.allSettled([
        axios.get(`${LOCAL_API_URL}/local-config`, { timeout: 2000 }),
        axios.get(`${LOCAL_API_URL}/sync/status`, { timeout: 2000 }),
      ]);

      if (configRes.status === "fulfilled") {
        const config = configRes.value.data || null;

        setCurrentConfig(config);

       const existingPort =
  config?.drawer_com_port ??
  config?.drawerComPort ??
  "";

setDrawerComPort(
  normalizeComPort(existingPort)
);
      } else {
        console.log(
          "Local config read failed:",
          configRes.reason?.response?.data || configRes.reason?.message
        );

        setCurrentConfig(null);
      }

      if (syncRes.status === "fulfilled") {
        setSyncStatus(syncRes.value.data || null);
      } else {
        console.log(
          "Sync status read failed:",
          syncRes.reason?.response?.data || syncRes.reason?.message
        );

        setSyncStatus({
          online: false,
          pendingSales: 0,
          message: "Could not read sync status",
        });
      }
    } catch (err) {
      console.error("Activation status load error:", err);
      setError("Could not load till status.");
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    loadCurrentStatus();
  }, [loadCurrentStatus]);

  const saveDrawerPort = async () => {
    const cleanPort = normalizeComPort(drawerComPort);

    setError("");
    setSuccess("");

    if (!isValidComPort(cleanPort)) {
      setError("Invalid drawer COM port. Example: COM4 or COM7.");
      return false;
    }

    try {
      setSavingPort(true);

      const res = await axios.post(
        `${LOCAL_API_URL}/local-config/drawer-port`,
        {
          drawer_com_port: cleanPort,
        },
        {
          timeout: 5000,
        }
      );

      console.log("Drawer port save response:", res.data);

      setDrawerComPort(cleanPort);
      setSuccess(
  cleanPort
    ? `Drawer port saved as ${cleanPort}.`
    : "Printer-connected drawer mode enabled."
);

      await loadCurrentStatus();

      return true;
    } catch (err) {
      console.error("Drawer port save error:", err.response?.data || err.message);

      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          "Failed to save drawer port"
      );

      return false;
    } finally {
      setSavingPort(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();

    const cleanKey = String(licenseKey || "").trim();
    const cleanPort = normalizeComPort(drawerComPort);

    setError("");
    setSuccess("");

    if (!cleanKey) {
      setError("Please enter a licence key.");
      return;
    }

    if (!isValidComPort(cleanPort)) {
      setError("Invalid drawer COM port. Example: COM4 or COM7.");
      return;
    }

    if (pendingSales > 0) {
      setError(
        `This till has ${pendingSales} unsynced sale(s). Please sync them before switching till.`
      );
      return;
    }

    const alreadyActivated = !!currentConfig?.license_key;
    const currentShopId = currentConfig?.shop_id;

    if (alreadyActivated) {
      const confirmed = window.confirm(
        `This till is currently activated for shop ${currentShopId}. Do you want to switch this till to another shop?`
      );

      if (!confirmed) return;
    }

    try {
      setActivating(true);

      const res = await axios.post(
        `${LOCAL_API_URL}/activate`,
        {
          license_key: cleanKey,
          drawer_com_port: cleanPort,
        },
        {
          timeout: 20000,
        },
      );

      console.log("Till activation response:", res.data);

      await axios.post(
        `${LOCAL_API_URL}/local-config/drawer-port`,
        {
          drawer_com_port: cleanPort,
        },
        {
          timeout: 5000,
        }
      );

      try {
        localStorage.removeItem("shop_config_cache");
      } catch {
        // ignore localStorage error
      }

      // Important: after switching till, old logged-in user may belong to old shop.
      logout();

      setSuccess(
        res.data?.message ||
          `Till activated successfully for shop ${
            res.data?.shop_id || ""
          }. Drawer port saved as ${cleanPort}.`
      );

      setLicenseKey("");
      setDrawerComPort(cleanPort);

      await loadCurrentStatus();
    } catch (err) {
      console.error("Till activation error:", err.response?.data || err.message);

      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.response?.data?.reason ||
          err.message ||
          "Activation failed"
      );
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="activation-page">
      <div className="activation-card">
        <div className="activation-logo">DP</div>

        <h1>Activate / Switch Till</h1>

        <p className="activation-subtitle">
          Enter the shop licence key to activate this till PC.
        </p>

        <div className="activation-status-box">
          <div className="activation-status-header">
            <h2>Current Till Status</h2>

            <button
              type="button"
              className="activation-small-btn"
              onClick={loadCurrentStatus}
              disabled={loadingStatus}
            >
              {loadingStatus ? "Checking..." : "Refresh"}
            </button>
          </div>

          <div className="activation-row">
            <span>Local Server</span>
            <strong>{syncStatus ? "Connected" : "Not Connected"}</strong>
          </div>

          <div className="activation-row">
            <span>Activated Shop ID</span>
            <strong>{currentConfig?.shop_id || "-"}</strong>
          </div>

          <div className="activation-row">
            <span>Shop Name</span>
            <strong>
              {currentConfig?.shop_name ||
                currentConfig?.shopName ||
                currentConfig?.receipt_name ||
                currentConfig?.receiptName ||
                "-"}
            </strong>
          </div>

          <div className="activation-row">
            <span>Current Licence</span>
            <strong>
              {maskLicence(
                currentConfig?.license_key || currentConfig?.licenseKey,
              )}
            </strong>
          </div>

          <div className="activation-row">
            <span>Drawer Port</span>
            <strong>
              {currentConfig?.drawer_com_port ||
                currentConfig?.drawerComPort ||
                drawerComPort ||
                "Printer Connected"}
            </strong>
          </div>

          <div className="activation-row">
            <span>Pending Sales</span>
            <strong className={pendingSales > 0 ? "danger-text" : ""}>
              {pendingSales}
            </strong>
          </div>

          <div className="activation-row">
            <span>Sync Message</span>
            <strong>{syncStatus?.message || "-"}</strong>
          </div>
        </div>

        {pendingSales > 0 && (
          <div className="activation-warning">
            This till has unsynced sales. Switching shop is blocked until sales
            are synced.
          </div>
        )}

        {error && <div className="activation-error">{error}</div>}

        {success && <div className="activation-success">{success}</div>}

        <form
          onSubmit={handleActivate}
          className="activation-form activation-login-form"
        >
          <label className="activation-input-label">
            Licence Key
            <input
              className="activation-input"
              type="text"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              placeholder="Enter licence key"
              autoFocus
            />
          </label>

          <label className="activation-input-label">
            Drawer COM Port
            <input
              className="activation-input"
              type="text"
              value={drawerComPort}
              onChange={(e) => setDrawerComPort(e.target.value.toUpperCase())}
              placeholder="COM4 / COM7, or leave blank for printer drawer"
            />
          </label>

          <button
            type="submit"
            disabled={activating || pendingSales > 0}
            className="activation-main-btn"
          >
            {activating ? "Activating..." : "Activate / Switch Till"}
          </button>

          <button
            type="button"
            disabled={savingPort}
            className="activation-secondary-btn"
            onClick={saveDrawerPort}
          >
            {savingPort ? "Saving Port..." : "Save Drawer Port Only"}
          </button>
        </form>

        <div className="activation-actions">
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
          >
            Go to POS Login
          </button>
        </div>
      </div>
    </div>
  );
}

export default ActivationPage;